import { AUTHORISED_CONTENT_ENABLED } from '~~/layers/core/app/util/features'

export const useEntryStore = defineStore('entryStore', {
    state: () => ({
        entry: {},
        seomatic: {},
        variables: {},
        seoHeadScripts: [],
        seoBodyScripts: [],
        seoBodyNoscripts: [],
        currentUri: '', // Track current URI for reload functionality
        currentArticle: { type: '', slug: '' }, // Track current article for reload
    }),
    actions: {
        async loadEntry(uri, options?: { routeQuery?: Record<string, any> }) {

            this.currentUri = uri
            this.currentArticle = { type: '', slug: '' }

            const { cmsQuery } = useGraphQL()

            const {data: pageData } = await cmsQuery({
                uri: `page-data/page/${uri}`,
                key: `entry-${uri}`,
                routeQuery: options?.routeQuery
            })

            // Check if there are errors in the response (could indicate protected content)
            const hasErrors = pageData.value?.errors?.length > 0
            if (hasErrors) {
                console.warn('CMS returned errors:', pageData.value?.errors)
            }

            const redirect = get(pageData.value?.data, 'retourResolveRedirect', {})

            if (redirect && !isEmpty(redirect.redirectDestUrl) && redirect.enabled) {
                const routeQuery = options?.routeQuery ?? useRoute().query
                return navigateTo({
                    path: redirect.redirectDestUrl,
                    query: routeQuery
                }, {
                    redirectCode: redirect.redirectHttpCode || 301,
                    replace: true,
                    external: true
                })
            }

            // replace page data entry
            this.entry = get(pageData.value?.data, 'entry', {})

            // Check if content is protected - don't throw 404 for protected content
            if (AUTHORISED_CONTENT_ENABLED) {
                const authorisedContent = get(this.entry, 'authorisedContent', {})
                if (authorisedContent.isProtected && !authorisedContent.isAuthorized) {
                    // Content exists but is protected - allow rendering with gate UI
                    return
                }
            }

            // If entry is null/empty but we had errors, it might be protected content
            // Don't throw 404 on client-side - allow gate UI to attempt authorization
            if (isEmpty(this.entry?.slug)) {
                // On SSR, we can't access localStorage tokens, so protected pages will fail
                // Only throw 404 if we're certain the page doesn't exist (no errors)
                if (!hasErrors) {
                    throw createError({
                        statusMessage: 'Page not found',
                        message: 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                        statusCode: 404,
                    })
                }

                // TODO: check why we need this
                // If there were errors and entry is null, it might be protected
                // Store minimal info so the page can show a gate UI
                if (AUTHORISED_CONTENT_ENABLED) {
                    console.log('Entry is null with errors - might be protected content')
                    this.entry = {
                        uri,
                        potentiallyProtected: true,
                    }
                    return
                }
            }

            // Handle RedirectPage entries
            if (this.entry?.__typename === 'ds_RedirectPage_Entry') {
                const redirectUrl = this.entry?.redirectLink?.[0].linkUrl

                if (redirectUrl) {
                    const routeQuery = options?.routeQuery ?? useRoute().query
                    return navigateTo({
                        path: redirectUrl,
                        query: routeQuery
                    }, {
                        redirectCode: 301,
                        replace: true,
                        external: true
                    })
                }
            }

            this.initEntry()
        },

        async loadArticle(type: string, slug: string) {
            this.currentArticle = { type, slug }
            this.currentUri = ''

            const { cmsQuery } = useGraphQL()

            const {data: articleData } = await cmsQuery({
                uri: `page-data/article/${type}/${slug}`,
                key: `entry-${type}-${slug}`,
            })


            // Check if there are errors in the response (could indicate protected content)
            const hasErrors = articleData.value?.errors?.length > 0
            if (hasErrors) {
                console.warn('CMS returned errors:', articleData.value?.errors)
            }

            // replace page data entry
            this.entry = get(articleData.value?.data, 'entry', {})

            // Check if content is protected - don't throw 404 for protected content
            if (AUTHORISED_CONTENT_ENABLED) {
                const authorisedContent = get(this.entry, 'authorisedContent', {})
                if (authorisedContent.isProtected && !authorisedContent.isAuthorized) {
                    // Content exists but is protected - allow rendering with gate UI
                    return
                }
            }

            // If entry is null/empty but we had errors, it might be protected content
            if (isEmpty(this.entry?.slug)) {
                if (!hasErrors) {
                    throw createError({
                        statusMessage: 'Page not found',
                        message: 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                        statusCode: 404,
                    })
                }
                // If there were errors and entry is null, it might be protected
                if (AUTHORISED_CONTENT_ENABLED) {
                    console.log('Article entry is null with errors - might be protected content')
                    this.entry = { slug, type, potentiallyProtected: true }
                    return
                }
            }

            this.initEntry()
        },

        initEntry() {
            this.seomatic = get(this.entry, 'seomatic', {})
            this.variables = JSON.parse(get(this.seomatic, 'metaSiteVarsContainer', '{}'))

            const scripts = Object.values(JSON.parse(get(this.seomatic, 'metaScriptContainer', '{}')))

            // Separate head and body scripts
            this.seoHeadScripts = scripts
                .filter(script => script.script)
                .map(script => ({
                    innerHTML: script.script,
                    type: 'text/javascript'
                }))

            // Parse body scripts from HTML strings, extracting script tags and their attributes
            this.seoBodyScripts = scripts
                .filter(script => script.bodyScript)
                .flatMap(script =>
                    Array.from(script.bodyScript.matchAll(/<script([^>]*)>(.*?)<\/script>/gs), match => {
                        const [, attributes, content] = match
                        const srcMatch = attributes.match(/src=["']([^"']+)["']/)
                        const typeMatch = attributes.match(/type=["']([^"']+)["']/)

                        return {
                            tagPosition: 'bodyClose',
                            ...(srcMatch && { src: srcMatch[1] }),
                            ...(attributes.includes('async') && { async: true }),
                            ...(attributes.includes('defer') && { defer: true }),
                            ...(typeMatch && { type: typeMatch[1] }),
                            ...(content.trim() && { innerHTML: content.trim() })
                        }
                    })
                )

            // Extract noscript tags from body scripts
            this.seoBodyNoscripts = scripts
                .filter(script => script.bodyScript)
                .flatMap(script =>
                    Array.from(script.bodyScript.matchAll(/<noscript>(.*?)<\/noscript>/gs), match => ({
                        innerHTML: match[1].trim(),
                        tagPosition: 'bodyClose'
                    }))
                )
        },



        /**
         * Reload the current entry with fresh data.
         * Used after authorization to fetch protected content with the new token.
         * Forces a client-side fetch to bypass cache and include new gate tokens.
         */
        async reloadEntry() {
            const { cmsQuery } = useGraphQL()

            if (this.currentArticle.type && this.currentArticle.slug) {
                const { type, slug } = this.currentArticle

                // When server: false, cmsQuery returns { data: graphqlData } directly (not a ref)
                // graphqlData contains { entry: {...} }
                const result = await cmsQuery({
                    uri: `page-data/article/${type}/${slug}`,
                    key: `entry-${type}-${slug}-reload-${Date.now()}`,
                    server: false, // Force client-side fetch
                })

                console.log('[reloadEntry] article result:', result)
                this.entry = get(result?.data, 'entry', {})
                this.initEntry()
                return
            }

            if (this.currentUri) {
                // When server: false, cmsQuery returns { data: graphqlData } directly (not a ref)
                // graphqlData contains { entry: {...} }
                const result = await cmsQuery({
                    uri: `page-data/page/${this.currentUri}`,
                    key: `entry-${this.currentUri}-reload-${Date.now()}`,
                    server: false, // Force client-side fetch
                })

                console.log('[reloadEntry] page result:', result)
                this.entry = get(result?.data, 'entry', {})
                this.initEntry()
                return
            }

            console.warn('Cannot reload entry: no URI or article info stored')
        },


    },
})